import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownStyled } from 'src/components';
import { sendCartToApAtom } from 'src/store';

type Props = {
  forGetHeader: boolean;
  cartVendor?: string;
  cartLocale?: string;
  cartAp?: string;
  showLocationSelector: boolean;
  setShowLocationSelector: (show: boolean) => void;
};

export const LocationSelector = ({
  forGetHeader,
  cartVendor,
  cartLocale,
  cartAp,
  showLocationSelector,
  setShowLocationSelector,
}: Props) => {
  const [sendCartToAp, setSendCartToAp] = useAtom(sendCartToApAtom);

  const { t } = useTranslation();

  // if all the conditions are met, show the location selector
  useEffect(() => {
    if (
      cartVendor?.toLowerCase() === 'walmart' &&
      cartLocale?.toLowerCase() === 'us' &&
      cartAp
    ) {
      setShowLocationSelector(true);
    } else {
      setShowLocationSelector(false);
    }
  }, [cartVendor, cartLocale, cartAp, setShowLocationSelector]);

  // if showLocationSelector is off or we are not hydrating the Get Header, return null
  if (!showLocationSelector || !forGetHeader) {
    return null;
  }

  return (
    <div className='flex flex-col flex-nowrap items-end justify-center gap-1.5 self-end xs:flex-row xs:items-center'>
      <p className='text-100 text-offWhite'>{t('get.store_location')}</p>
      <DropdownStyled
        className={'w-[150px] min-w-[150px] max-w-[150px]'}
        customHeight='h-8'
        buttonContent={
          <span className='text-100 whitespace-nowrap text-offWhite'>
            {sendCartToAp ? t('get.cart_senders') : t('get.closest_to_me')}
          </span>
        }
      >
        {(closeDropdown) => (
          <div className='z-20 flex min-w-[200px] flex-col items-start justify-center gap-4 rounded-lg bg-cloudGray py-4 pl-6 pr-4 xs:min-w-[240px]'>
            <button
              className='group flex w-full flex-col items-start justify-center gap-1 whitespace-nowrap py-0.5 text-left transition-transform'
              onClick={() => {
                setSendCartToAp(false);
                closeDropdown();
              }}
            >
              <span
                className={`${!sendCartToAp ? 'text-primary-dark' : 'text-primary'} text-left font-secondary text-base font-semibold leading-[25.6px] transition-colors group-hover:text-primary-dark`}
              >
                {t('get.closest_to_me')}
              </span>
              <span className='font-base whitespace-pre-wrap font-secondary text-sm leading-[18px] text-primary-dark'>
                {t('get.walmart_will_choose_the_closest_store_to_you')}
              </span>
            </button>
            <button
              className='group flex w-full flex-col items-start justify-center gap-1 whitespace-nowrap py-0.5 text-left transition-transform'
              onClick={() => {
                setSendCartToAp(true);
                closeDropdown();
              }}
            >
              <span
                className={`${sendCartToAp ? 'text-primary-dark' : 'text-primary'} text-left font-secondary text-base font-semibold leading-[25.6px] transition-colors group-hover:text-primary-dark`}
              >
                {t('get.cart_senders')}
              </span>
              <span className='font-base whitespace-pre-wrap font-secondary text-sm leading-[18px] text-primary-dark'>
                {t('get.cart_will_load_with_the_senders_local_store_selected')}
              </span>
            </button>
          </div>
        )}
      </DropdownStyled>
    </div>
  );
};
